import React, { useMemo, useState, useEffect } from 'react';
import {
  Plus,
  Edit,
  Trash2,
  Eye,
  Search,
  Filter,
  ArrowUpDown,
  Building2,
  CheckCircle2,
  PauseCircle,
  Network,
  ChevronRight,
  ChevronDown,
  CornerDownRight
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '../ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { workspaceAdminAPI } from '../../services/api';
import { PROXY_CANCELLED } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/utils';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip
} from 'recharts';

import { useNavigate } from 'react-router-dom';
import { useTableControls } from '../../hooks/useTableControls';
import { TableControls } from '../ui/table-controls';
import { EmptyState, CardSkeleton } from '../common/States';
import { StatCard } from '../common/StatCard';
import { chartColor, axisProps, gridProps, tooltipContentStyle, tooltipCursor } from '../../lib/chartTheme';
import {
  groupChildrenByParent,
  selectTopLevelTenants,
  selectVisibleChildren,
  parentsToAutoExpand,
} from '../../lib/tenantTree';

const resolvePlanCode = (plans, currentValue = '') => {
  const normalized = String(currentValue || '').trim();
  if (normalized && plans.some((plan) => String(plan.code || '').trim() === normalized)) {
    return normalized;
  }
  return plans[0]?.code || '';
};

const TENANT_SECTIONS = [
  {
    key: 'comparison-chart',
    id: 'tenants/comparison-chart',
    label: 'Tenant User Comparison (Top 8)',
    description: 'Compare top tenants by users or utilization',
  },
  {
    key: 'comparison-table',
    id: 'tenants/comparison-table',
    label: 'Tenant Comparison Table',
    description: 'Review sortable tenant comparison metrics',
  },
  {
    key: 'list',
    id: 'tenants/list',
    label: 'Tenants',
    description: 'Manage tenants with view, edit, and delete actions',
  },
];

const TenantsCRUD = ({ activeTab = 'tenants/comparison-chart', onNavigate, onProxyLogin, onCreateTenant }) => {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [users, setUsers] = useState([]);
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chartMode, setChartMode] = useState('users');
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    max_users: 10,
    plan: '',
    status: 'active'
  });

  useEffect(() => {
    loadTenants();
  }, []);

  const loadTenants = async () => {
    try {
      setLoading(true);
      const [tenantsResponse, usersResponse, plansResponse] = await Promise.all([
        workspaceAdminAPI.listTenants(),
        workspaceAdminAPI.listUsers(),
        workspaceAdminAPI.listSubscriptionPlans({ include_inactive: true }),
      ]);
      const plansPayload = plansResponse?.data;
      const plans = Array.isArray(plansPayload)
        ? plansPayload
        : Array.isArray(plansPayload?.items)
          ? plansPayload.items
          : [];
      const normalizedPlans = plans
        .map((plan) => ({
          id: plan?.id,
          code: String(plan?.code || '').trim(),
          name: String(plan?.name || '').trim(),
        }))
        .filter((plan) => Boolean(plan.code || plan.name))
        .map((plan) => ({
          ...plan,
          code: plan.code || `plan_${plan.id ?? 'unknown'}`,
          name: plan.name || plan.code || `Plan ${plan.id ?? ''}`.trim(),
        }));
      const planCatalog = normalizedPlans;
      setTenants(Array.isArray(tenantsResponse.data) ? tenantsResponse.data : []);
      setUsers(Array.isArray(usersResponse.data) ? usersResponse.data : []);
      setSubscriptionPlans(planCatalog);
      setFormData((prev) => ({
        ...prev,
        plan: resolvePlanCode(planCatalog, prev.plan),
      }));
    } catch (error) {
      console.error('Failed to load tenants:', error);
      toast.error(getErrorMessage(error, 'Failed to load tenants'));
      setSubscriptionPlans([]);
      setFormData((prev) => ({
        ...prev,
        plan: '',
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      await workspaceAdminAPI.createTenant(formData);
      setIsCreateDialogOpen(false);
      setFormData({
        name: '',
        slug: '',
        description: '',
        max_users: 10,
        plan: resolvePlanCode(subscriptionPlans, ''),
        status: 'active'
      });
      await loadTenants();
      toast.success('Tenant created successfully');
    } catch (error) {
      console.error('Failed to create tenant:', error);
      toast.error(getErrorMessage(error, 'Failed to create tenant'));
    }
  };

  const handleDelete = async () => {
    try {
      await workspaceAdminAPI.deleteTenant(selectedTenant.id);
      setIsDeleteDialogOpen(false);
      setSelectedTenant(null);
      await loadTenants();
      toast.success('Tenant deleted successfully');
    } catch (error) {
      console.error('Failed to delete tenant:', error);
      toast.error(getErrorMessage(error, 'Failed to delete tenant'));
    }
  };

  const handleEditClick = (tenant) => {
    navigate(`/superadmin/tenants/edit/${tenant.id}`);
  };

  const handleDeleteClick = (tenant) => {
    setSelectedTenant(tenant);
    setIsDeleteDialogOpen(true);
  };

  const handleProxyLogin = async (tenant) => {
    try {
      const session = await onProxyLogin(tenant.slug);
      const role = String(session?.role || '').toLowerCase();
      if (role === 'superadmin') {
        toast.success(`Already in ${tenant.name}`);
        navigate('/superadmin');
        return;
      }
      toast.success(`Proxy login successful: ${tenant.name}`);
      navigate('/admin');
    } catch (error) {
      if (error?.code === PROXY_CANCELLED) return;
      console.error('Proxy login failed:', error);
      toast.error(getErrorMessage(error, 'Proxy login failed'));
    }
  };

  const filteredTenants = tenants.filter(tenant =>
    tenant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tenant.slug.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Top-level tenants are the rows; sub-tenants are revealed by expanding one.
  // `parent_tenant_id` comes from GET /tenants. Grouping lives in lib/tenantTree
  // because its failure mode is a tenant silently not rendering.
  const childrenByParent = useMemo(() => groupChildrenByParent(tenants), [tenants]);

  const topLevelTenants = useMemo(
    () => selectTopLevelTenants(tenants, searchTerm),
    [tenants, searchTerm]
  );

  const [expandedTenantIds, setExpandedTenantIds] = useState(() => new Set());

  const toggleTenantExpanded = (tenantId) => {
    setExpandedTenantIds((prev) => {
      const next = new Set(prev);
      if (next.has(tenantId)) next.delete(tenantId);
      else next.add(tenantId);
      return next;
    });
  };

  // Open any parent holding a match, so a hit inside a collapsed parent is not
  // mistaken for no result.
  useEffect(() => {
    if (!searchTerm.trim()) return;
    setExpandedTenantIds(parentsToAutoExpand(tenants, searchTerm));
  }, [searchTerm, tenants]);

  const comparisonRows = useMemo(() => {
    const userCountByTenant = users.reduce((acc, user) => {
      const tenantId = user.tenant_id;
      if (!tenantId) return acc;
      acc[tenantId] = (acc[tenantId] || 0) + 1;
      return acc;
    }, {});

    const activeUsersByTenant = users.reduce((acc, user) => {
      const tenantId = user.tenant_id;
      if (!tenantId || !user.is_active) return acc;
      acc[tenantId] = (acc[tenantId] || 0) + 1;
      return acc;
    }, {});

    return tenants.map((tenant) => {
      const totalUsers = userCountByTenant[tenant.id] || 0;
      const activeUsers = activeUsersByTenant[tenant.id] || 0;
      const maxUsers = tenant.max_users || 0;
      const utilizationPct = maxUsers > 0 ? Math.min((totalUsers / maxUsers) * 100, 100) : 0;
      return {
        id: tenant.id,
        name: tenant.name,
        slug: tenant.slug,
        status: tenant.status || 'active',
        plan: tenant.subscription_plan_code || tenant.subscription_plan_name || tenant.plan || 'unassigned',
        maxUsers,
        totalUsers,
        activeUsers,
        utilizationPct: Number(utilizationPct.toFixed(1)),
      };
    }).sort((a, b) => b.totalUsers - a.totalUsers);
  }, [tenants, users]);

  const summaryStats = useMemo(() => {
    const total = tenants.length;
    const active = tenants.filter((tenant) => String(tenant.status || 'active').toLowerCase() === 'active').length;
    const suspended = tenants.filter((tenant) => String(tenant.status || '').toLowerCase() === 'suspended').length;
    const childEnabled = tenants.filter((tenant) => Boolean(tenant.parent_tenant_id)).length;
    return { total, active, suspended, childEnabled };
  }, [tenants]);

  const filteredComparisonRows = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    if (!normalizedSearch) {
      return comparisonRows;
    }
    return comparisonRows.filter((row) =>
      String(row.name || '').toLowerCase().includes(normalizedSearch) ||
      String(row.slug || '').toLowerCase().includes(normalizedSearch)
    );
  }, [comparisonRows, searchTerm]);

  const chartData = useMemo(() => {
    return filteredComparisonRows.slice(0, 8).map((row) => ({
      name: row.name.length > 12 ? `${row.name.slice(0, 12)}...` : row.name,
      users: row.totalUsers,
      active: row.activeUsers,
      utilizationPct: row.utilizationPct,
    }));
  }, [filteredComparisonRows]);

  const comparisonByTenantId = useMemo(() => {
    return new Map(comparisonRows.map((row) => [row.id, row]));
  }, [comparisonRows]);

  const comparisonTable = useTableControls(filteredComparisonRows, {
    initialSortKey: 'totalUsers',
    initialSortDirection: 'desc',
  });

  const currentSectionKey = useMemo(() => {
    const rawSection = String(activeTab || '')
      .replace(/^tenants\/?/, '')
      .trim()
      .toLowerCase();
    if (!rawSection || rawSection === 'tenants') {
      return 'comparison-chart';
    }
    return TENANT_SECTIONS.some((section) => section.key === rawSection) ? rawSection : 'comparison-chart';
  }, [activeTab]);

  const currentSection = useMemo(
    () => TENANT_SECTIONS.find((section) => section.key === currentSectionKey) || TENANT_SECTIONS[0],
    [currentSectionKey]
  );

  const navigationCards = useMemo(
    () => [
      {
        ...TENANT_SECTIONS.find((section) => section.key === 'comparison-chart'),
        metric: `${Math.min(chartData.length, 8)} tenants visualized`,
      },
      {
        ...TENANT_SECTIONS.find((section) => section.key === 'comparison-table'),
        metric: `${filteredComparisonRows.length} comparison rows`,
      },
      {
        ...TENANT_SECTIONS.find((section) => section.key === 'list'),
        metric: `${filteredTenants.length} tenants listed`,
      },
    ],
    [chartData.length, filteredComparisonRows.length, filteredTenants.length]
  );

  const renderSortableHeader = (label, key) => (
    <button
      type="button"
      className="inline-flex items-center gap-1 font-medium hover:text-primary"
      onClick={() => comparisonTable.onSort(key)}
    >
      {label}
      <ArrowUpDown className="h-3.5 w-3.5" />
    </button>
  );

  const getUtilizationBarClass = (utilizationPct) => {
    if (utilizationPct < 60) return 'bg-[#10B981]';
    if (utilizationPct <= 85) return 'bg-[#F59E0B]';
    return 'bg-[#EF4444]';
  };

  const renderComparisonChartSection = () => (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-hr-lg">
          {chartMode === 'users' ? 'Tenant User Comparison (Top 8)' : 'Tenant Utilization Comparison (Top 8)'}
        </CardTitle>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant={chartMode === 'users' ? 'default' : 'outline'}
            onClick={() => setChartMode('users')}
          >
            Users
          </Button>
          <Button
            size="sm"
            variant={chartMode === 'utilization' ? 'default' : 'outline'}
            onClick={() => setChartMode('utilization')}
          >
            Utilization %
          </Button>
        </div>
      </CardHeader>
      <CardContent className="h-[320px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData}>
            <CartesianGrid {...gridProps} />
            <XAxis dataKey="name" {...axisProps} />
            <YAxis
              allowDecimals={false}
              domain={chartMode === 'utilization' ? [0, 100] : undefined}
              {...axisProps}
            />
            <Tooltip contentStyle={tooltipContentStyle} cursor={tooltipCursor} />
            {chartMode === 'users' ? (
              <>
                <Bar dataKey="users" fill={chartColor(0)} radius={[8, 8, 0, 0]} />
                <Bar dataKey="active" fill={chartColor(1)} radius={[8, 8, 0, 0]} />
              </>
            ) : (
              <Bar dataKey="utilizationPct" fill={chartColor(4)} radius={[8, 8, 0, 0]} />
            )}
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );

  const renderComparisonTableSection = () => (
    <Card>
      <CardHeader>
        <CardTitle className="text-hr-lg">Tenant Comparison Table</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b">
                <th className="text-left p-3">{renderSortableHeader('Tenant', 'name')}</th>
                <th className="text-left p-3">{renderSortableHeader('Plan', 'plan')}</th>
                <th className="text-left p-3">{renderSortableHeader('Users', 'totalUsers')}</th>
                <th className="text-left p-3">{renderSortableHeader('Active', 'activeUsers')}</th>
                <th className="text-left p-3">{renderSortableHeader('Max', 'maxUsers')}</th>
                <th className="text-left p-3">{renderSortableHeader('Utilization', 'utilizationPct')}</th>
                <th className="text-left p-3">{renderSortableHeader('Status', 'status')}</th>
              </tr>
            </thead>
            <tbody>
              {comparisonTable.data.length === 0 ? (
                <tr>
                  <td colSpan={7} className="p-0">
                    <EmptyState
                      icon={Building2}
                      title="No tenants to compare"
                      description="Adjust your search to see tenant comparison metrics."
                    />
                  </td>
                </tr>
              ) : comparisonTable.data.map((row) => (
                <tr key={row.id} className="border-b last:border-0 card-interactive hover:bg-muted/40">
                  <td className="p-3">
                    <div className="font-medium">{row.name}</div>
                    <div className="text-hr-xs text-muted-foreground">{row.slug}</div>
                  </td>
                  <td className="p-3">
                    <Badge variant="outline">{row.plan}</Badge>
                  </td>
                  <td className="p-3">{row.totalUsers}</td>
                  <td className="p-3">{row.activeUsers}</td>
                  <td className="p-3">{row.maxUsers || 'N/A'}</td>
                  <td className="p-3">
                    {row.maxUsers > 0 ? `${row.utilizationPct}%` : 'N/A'}
                  </td>
                  <td className="p-3">
                    <Badge variant={row.status === 'active' ? 'success' : row.status === 'pending' ? 'warning' : 'danger'}>
                      {row.status}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
      {filteredComparisonRows.length > 0 && (
        <TableControls
          totalRows={comparisonTable.totalRows}
          page={comparisonTable.page}
          totalPages={comparisonTable.totalPages}
          pageSize={comparisonTable.pageSize}
          pageSizes={comparisonTable.pageSizes}
          onPageSizeChange={comparisonTable.onPageSizeChange}
          onPrevious={comparisonTable.goToPreviousPage}
          onNext={comparisonTable.goToNextPage}
          exportConfig={{
            title: 'Tenant Comparison',
            fileName: `tenant-comparison-${new Date().toISOString().split('T')[0]}`,
            rows: filteredComparisonRows,
            columns: [
              { label: 'Tenant', accessor: (row) => row.name },
              { label: 'Slug', accessor: (row) => row.slug },
              { label: 'Plan', accessor: (row) => row.plan },
              { label: 'Users', accessor: (row) => row.totalUsers },
              { label: 'Active', accessor: (row) => row.activeUsers },
              { label: 'Max', accessor: (row) => row.maxUsers || 'N/A' },
              { label: 'Utilization', accessor: (row) => (row.maxUsers > 0 ? `${row.utilizationPct}%` : 'N/A') },
              { label: 'Status', accessor: (row) => row.status },
            ],
          }}
        />
      )}
    </Card>
  );

  const renderTenantRow = (tenant, { isChild = false } = {}) => {
    const metrics = comparisonByTenantId.get(tenant.id);
    const status = metrics?.status || tenant.status || 'active';
    const plan = metrics?.plan || tenant.plan || 'basic';
    const totalUsers = metrics?.totalUsers ?? 0;
    const activeUsers = metrics?.activeUsers ?? 0;
    const maxUsers = metrics?.maxUsers ?? 0;
    const utilizationPct = metrics?.utilizationPct ?? 0;
    const children = childrenByParent.get(tenant.id) || [];
    const isExpanded = expandedTenantIds.has(tenant.id);

    return (
      <tr
        key={tenant.id}
        className={`border-b last:border-0 card-interactive hover:bg-muted/40 ${isChild ? 'bg-muted/20' : ''}`}
      >
        <td className="p-3">
          <div className="flex items-center gap-2">
            {isChild ? (
              // Aligns the child under its parent's name, past the toggle.
              <span className="w-6 shrink-0" aria-hidden="true" />
            ) : children.length > 0 ? (
              <button
                type="button"
                onClick={() => toggleTenantExpanded(tenant.id)}
                className="flex h-6 w-6 shrink-0 items-center justify-center rounded hover:bg-muted"
                aria-expanded={isExpanded}
                aria-label={`${isExpanded ? 'Hide' : 'Show'} sub-tenants of ${tenant.name}`}
                title={`${isExpanded ? 'Hide' : 'Show'} ${children.length} sub-tenant${children.length > 1 ? 's' : ''}`}
              >
                {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </button>
            ) : (
              <span className="w-6 shrink-0" aria-hidden="true" />
            )}

            {isChild && <CornerDownRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}

            <div className={isChild ? '' : 'font-medium'}>{tenant.name}</div>

            {!isChild && children.length > 0 && (
              <Badge variant="outline" className="ml-1">
                {children.length} sub
              </Badge>
            )}
          </div>
        </td>
        <td className="p-3 text-muted-foreground">{tenant.slug}</td>
        <td className="p-3">
          <Badge variant="outline">{plan}</Badge>
        </td>
        <td className="p-3">{totalUsers}</td>
        <td className="p-3">{activeUsers}</td>
        <td className="p-3">{maxUsers || 'N/A'}</td>
        <td className="p-3">
          <div className="min-w-[120px]">
            <div className="mb-1 text-xs font-medium">
              {maxUsers > 0 ? `${utilizationPct}%` : 'N/A'}
            </div>
            <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
              <div
                className={`h-2 rounded-full transition-all duration-300 ${getUtilizationBarClass(utilizationPct)}`}
                style={{ width: `${Math.min(utilizationPct, 100)}%` }}
              />
            </div>
          </div>
        </td>
        <td className="p-3">
          <Badge variant={status === 'active' ? 'success' : status === 'pending' ? 'warning' : 'danger'}>
            {status}
          </Badge>
        </td>
        <td className="p-3">
          <div className="flex flex-wrap gap-2">
            <Button
              variant="default"
              size="sm"
              onClick={() => handleProxyLogin(tenant)}
              className="h-8 w-8 p-0"
              title="View"
              aria-label={`View ${tenant.name}`}
            >
              <Eye className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleEditClick(tenant)}
              className="h-8 w-8 p-0"
              title="Edit"
              aria-label={`Edit ${tenant.name}`}
            >
              <Edit className="h-4 w-4" />
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => handleDeleteClick(tenant)}
              className="h-8 w-8 p-0"
              title="Delete"
              aria-label={`Delete ${tenant.name}`}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </td>
      </tr>
    );
  };

  const renderTenantsSection = () => (
    <>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Total Tenants" value={summaryStats.total} icon={Building2} tone="primary" />
        <StatCard label="Active" value={summaryStats.active} icon={CheckCircle2} tone="success" />
        <StatCard label="Suspended" value={summaryStats.suspended} icon={PauseCircle} tone="warning" />
        <StatCard label="Child-enabled" value={summaryStats.childEnabled} icon={Network} tone="info" />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-hr-lg">Tenants</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-3 font-medium">Tenant</th>
                  <th className="text-left p-3 font-medium">Slug</th>
                  <th className="text-left p-3 font-medium">Plan</th>
                  <th className="text-left p-3 font-medium">Users</th>
                  <th className="text-left p-3 font-medium">Active</th>
                  <th className="text-left p-3 font-medium">Max</th>
                  <th className="text-left p-3 font-medium">Utilization</th>
                  <th className="text-left p-3 font-medium">Status</th>
                  <th className="text-left p-3 font-medium">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredTenants.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="p-0">
                      <EmptyState
                        icon={Building2}
                        title="No tenants found"
                        description="No tenants match the current filter. Try a different search or add a new tenant."
                        actionLabel="Add Tenant"
                        onAction={handleOpenCreate}
                      />
                    </td>
                  </tr>
                ) : topLevelTenants.flatMap((tenant) => {
                  const rows = [renderTenantRow(tenant)];
                  if (expandedTenantIds.has(tenant.id)) {
                    selectVisibleChildren(tenant, childrenByParent, searchTerm).forEach((child) => {
                      rows.push(renderTenantRow(child, { isChild: true }));
                    });
                  }
                  return rows;
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </>
  );

  const renderActiveSection = () => {
    switch (currentSectionKey) {
      case 'comparison-table':
        return renderComparisonTableSection();
      case 'list':
        return renderTenantsSection();
      case 'comparison-chart':
      default:
        return renderComparisonChartSection();
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <CardSkeleton rows={2} />
        <CardSkeleton rows={6} />
      </div>
    );
  }

  const handleOpenCreate = () => {
    if (typeof onCreateTenant === 'function') {
      onCreateTenant();
      return;
    }
    setIsCreateDialogOpen(true);
  };
  const effectivePlans = subscriptionPlans.length > 0 ? subscriptionPlans : [];
  const selectedPlanCode = resolvePlanCode(effectivePlans, formData.plan);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold">Tenant Management</h2>
          <p className="text-muted-foreground">Manage all organizations in the system</p>
        </div>
        <Button onClick={handleOpenCreate}>
          <Plus className="h-4 w-4 mr-2" />
          Add Tenant
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search tenants..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button variant="outline" className="gap-2">
              <Filter className="h-4 w-4" />
              Filter
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="space-y-3">
        <div>
          <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Tenants</p>
          <h3 className="mt-2 text-2xl font-semibold tracking-tight">{currentSection.label}</h3>
          <p className="mt-2 text-sm text-muted-foreground">{currentSection.description}</p>
        </div>

        {currentSectionKey === 'comparison-chart' ? (
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {navigationCards.map((section) => {
              const isActive = section.key === currentSectionKey;
              return (
                <button
                  key={section.id}
                  type="button"
                  onClick={() => onNavigate?.(section.id)}
                  className={`card-interactive rounded-2xl border p-4 text-left transition ${
                    isActive
                      ? 'border-primary bg-primary text-primary-foreground shadow-sm'
                      : 'border-border bg-card text-foreground hover:border-primary/40 hover:bg-muted/50'
                  }`}
                >
                  <p className={`text-xs font-medium uppercase tracking-[0.18em] ${isActive ? 'text-primary-foreground/80' : 'text-muted-foreground'}`}>
                    {section.label}
                  </p>
                  <p className={`mt-2 text-sm font-semibold ${isActive ? 'text-primary-foreground' : 'text-foreground'}`}>
                    {section.metric}
                  </p>
                  <p className={`mt-3 text-sm ${isActive ? 'text-primary-foreground/80' : 'text-muted-foreground'}`}>
                    {section.description}
                  </p>
                </button>
              );
            })}
          </div>
        ) : null}
      </div>

      {renderActiveSection()}

      {/* Create Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Tenant</DialogTitle>
            <DialogDescription>
              Add a new organization to the system. This will create a new workspace.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Organization Name</Label>
              <Input
                id="name"
                placeholder="Enter organization name"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="slug">Slug</Label>
              <Input
                id="slug"
                placeholder="Enter unique slug"
                value={formData.slug}
                onChange={(e) => setFormData({...formData, slug: e.target.value})}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                placeholder="Enter description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="max_users">Max Users</Label>
                <Input
                  id="max_users"
                  type="number"
                  value={formData.max_users}
                  onChange={(e) => setFormData({...formData, max_users: parseInt(e.target.value, 10) || 0})}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="plan">Plan</Label>
                <Select
                  value={selectedPlanCode}
                  onValueChange={(value) => setFormData({ ...formData, plan: value })}
                >
                  <SelectTrigger id="plan" className="w-full">
                    <SelectValue placeholder="Select Plan" />
                  </SelectTrigger>
                  <SelectContent>
                  {effectivePlans.length === 0 ? (
                    <SelectItem value="__no_plans_available" disabled>
                      No subscription plans available
                    </SelectItem>
                  ) : effectivePlans.map((plan, index) => (
                    <SelectItem key={`${plan.code}-${plan.id ?? index}`} value={plan.code}>
                      {plan.name || plan.code}
                    </SelectItem>
                  ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <select
                id="status"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                value={formData.status}
                onChange={(e) => setFormData({...formData, status: e.target.value})}
              >
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
                <option value="pending">Pending</option>
              </select>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreate}>Create Tenant</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the "{selectedTenant?.name}" 
              organization and all its data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TenantsCRUD;
